/** Numeric input step — field or stepper mode with optional unit and sig-fig validation. */
import { useEffect, useRef, useState } from 'react';
import { useSession } from '../SessionContext';
import { StepShell } from './StepShell';
import {
  PromptRenderer,
  type TiptapNode,
  type MediaItem,
} from '@/shared/components/PromptRenderer';
import { normaliseMediaRefs } from '../utils/normaliseMediaRefs';
import { FeedbackDisplay } from './FeedbackDisplay';
import { Button } from '@/interfaces/session-player/ui';
import { ChevronDown, Minus, Plus } from 'lucide-react';

interface DiagramJson {
  syntax: string;
  diagramType?: string;
}

interface NumericPublicConfig {
  placeholder?: string | null;
  integerOnly?: boolean;
  decimalPlaces?: number | null;
  inputMode?: 'field' | 'stepper';
  unit?: string | null;
  unitPosition?: 'prefix' | 'suffix';
  unitOptions?: string[] | null;
  significantFigures?: number | null;
}

/**
 * Counts the number of significant figures in a numeric string.
 * Rules:
 * - Leading zeros don't count: "0.005" → 1
 * - Trailing zeros after decimal count: "9.80" → 3
 * - Zeros between non-zero digits count: "101" → 3
 * - Trailing zeros without decimal don't count: "500" → 1
 */
function countSignificantFigures(raw: string): number {
  // Strip leading minus sign
  const s = raw.replace(/^-/, '');

  // "0" without a decimal is 1 significant figure by convention
  if (/^0+$/.test(s)) return 1;

  const hasDecimal = s.includes('.');
  const [intPart, fracPart] = s.split('.');

  if (hasDecimal) {
    // Combine integer and fractional digits, strip leading zeros
    const allDigits = (intPart ?? '') + (fracPart ?? '');
    const stripped = allDigits.replace(/^0+/, '');
    return stripped.length || 1;
  } else {
    // No decimal: strip leading AND trailing zeros
    const stripped = intPart.replace(/^0+/, '').replace(/0+$/, '');
    return stripped.length || 1;
  }
}

/**
 * Numeric input step. Supports field and stepper modes, optional unit label or dropdown,
 * integer-only mode, and significant figures validation.
 */
export function NumericStep() {
  const { currentStep, state, dispatch } = useSession();

  const snapshot = currentStep?.stepSnapshotPublicJson;
  const config = (snapshot?.config ?? null) as unknown as NumericPublicConfig | null;

  const inputMode = config?.inputMode ?? 'field';
  const integerOnly = config?.integerOnly ?? false;
  const placeholder = config?.placeholder ?? '';
  const unit = config?.unit ?? null;
  const unitPosition = config?.unitPosition ?? 'suffix';
  const unitOptions = config?.unitOptions ?? null;
  const significantFigures = config?.significantFigures ?? null;

  const answer = state.answer?.type === 'numeric' ? state.answer : null;
  const phase = state.phase;
  const result = state.result;
  const isDisabled = phase === 'submitting' || phase === 'grading';
  const isGraded = phase === 'graded';

  // rawInput tracks the user's string for comma normalisation and sig-fig display
  const [rawInput, setRawInput] = useState<string>(
    answer?.value != null ? String(answer.value) : ''
  );
  const [selectedUnit, setSelectedUnit] = useState<string>(
    answer?.unit ?? unitOptions?.[0] ?? unit ?? ''
  );
  const [sigFigError, setSigFigError] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);
  const [unitPickerOpen, setUnitPickerOpen] = useState(false);
  const unitPickerRef = useRef<HTMLDivElement>(null);

  // Close unit picker on outside click
  useEffect(() => {
    if (!unitPickerOpen) return;
    function handleClick(e: MouseEvent) {
      if (unitPickerRef.current && !unitPickerRef.current.contains(e.target as Node)) {
        setUnitPickerOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [unitPickerOpen]);

  const mediaItems: MediaItem[] | null = normaliseMediaRefs(currentStep?.mediaRefsJson);

  const diagramJson = (currentStep as unknown as Record<string, unknown>)?.diagramJson as
    | DiagramJson
    | null
    | undefined;

  if (!currentStep || !snapshot) return null;

  function parseAndDispatch(raw: string, unitOverride?: string) {
    const normalised = raw.split(',').join('.');
    const num = normalised === '' ? null : Number(normalised);
    const parsedUnit = unitOverride ?? selectedUnit;

    // Sig fig validation
    if (significantFigures != null && normalised !== '' && num !== null && !isNaN(num)) {
      const sf = countSignificantFigures(normalised);
      if (sf !== significantFigures) {
        setSigFigError(
          `Answer must have exactly ${significantFigures} significant figure${significantFigures === 1 ? '' : 's'} (yours has ${sf})`
        );
      } else {
        setSigFigError(null);
      }
    } else {
      setSigFigError(null);
    }

    // Integer-only guard
    if (integerOnly && num !== null && !isNaN(num) && !Number.isInteger(num)) {
      return;
    }

    const finalValue = num === null || isNaN(num) ? null : num;
    dispatch({
      type: 'UPDATE_ANSWER',
      answer: {
        type: 'numeric',
        value: finalValue,
        ...(parsedUnit ? { unit: parsedUnit } : {}),
      },
    });
  }

  const decimalPlaces = config?.decimalPlaces ?? null;

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (isDisabled || isGraded) return;
    const raw = e.target.value;

    // Block decimal separators entirely in integer-only mode
    if (integerOnly && (raw.includes('.') || raw.includes(','))) return;

    // Enforce max decimal places: reject input that exceeds the limit
    if (decimalPlaces !== null && !integerOnly) {
      const normalised = raw.split(',').join('.');
      const dotIdx = normalised.indexOf('.');
      if (dotIdx !== -1) {
        const fracLen = normalised.length - dotIdx - 1;
        if (decimalPlaces === 0 || fracLen > decimalPlaces) return;
      }
    }

    setRawInput(raw);
    parseAndDispatch(raw);
  }

  function selectUnit(u: string) {
    setSelectedUnit(u);
    setUnitPickerOpen(false);
    parseAndDispatch(rawInput, u);
  }

  function handleStepperChange(delta: number) {
    if (isDisabled || isGraded) return;
    const current = answer?.value ?? 0;
    const next = current + delta;
    const nextStr = String(next);
    setRawInput(nextStr);
    dispatch({
      type: 'UPDATE_ANSWER',
      answer: {
        type: 'numeric',
        value: next,
        ...(selectedUnit ? { unit: selectedUnit } : {}),
      },
    });
    setSigFigError(null);
  }

  // ─── Styles (matching TextStep pattern) ─────────────────────────────────────

  const hasUnitAddon = !!unitOptions || !!unit;

  const boxStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    borderRadius: 'var(--f-radius-xl)',
    border: '2px solid var(--f-border)',
    backgroundColor: 'var(--f-bg-raised)',
    transition: 'border-color var(--f-duration-fast) ease',
    position: 'relative',
  };

  const inputStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0,
    padding: 'var(--f-space-4)',
    border: 'none',
    background: 'transparent',
    color: 'var(--f-fg)',
    fontFamily: 'var(--f-font-sans)',
    fontSize: 'var(--f-text-base)',
    lineHeight: 'var(--f-leading-relaxed)',
    outline: 'none',
    textAlign: inputMode === 'stepper' ? 'center' : 'left',
  };

  const unitLabelStyle: React.CSSProperties = {
    padding: '0 var(--f-space-4)',
    fontSize: 'var(--f-text-sm)',
    color: 'var(--f-fg-muted)',
    fontFamily: 'var(--f-font-sans)',
    whiteSpace: 'nowrap',
    userSelect: 'none',
  };

  // ─── Unit addon (inside the box) ──────────────────────────────────────────

  const divider = (
    <div
      style={{
        width: 1,
        alignSelf: 'stretch',
        backgroundColor: 'var(--f-border)',
        margin: '8px 0',
        flexShrink: 0,
      }}
    />
  );

  const unitAddon = unitOptions ? (
    <>
      {divider}
      <div ref={unitPickerRef} style={{ position: 'relative', flexShrink: 0 }}>
        <button
          type="button"
          onClick={() => {
            if (!isDisabled && !isGraded) setUnitPickerOpen((v) => !v);
          }}
          disabled={isDisabled || isGraded}
          aria-label="Select unit"
          aria-expanded={unitPickerOpen}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: '0 12px',
            height: '100%',
            border: 'none',
            background: 'transparent',
            color: 'var(--f-fg)',
            fontFamily: 'var(--f-font-sans)',
            fontSize: 'var(--f-text-sm)',
            fontWeight: 500,
            cursor: isDisabled || isGraded ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {selectedUnit}
          <ChevronDown
            size={12}
            style={{
              color: 'var(--f-fg-muted)',
              transition: 'transform var(--f-duration-fast) ease',
              transform: unitPickerOpen ? 'rotate(180deg)' : 'none',
            }}
          />
        </button>

        {/* Custom dropdown menu */}
        {unitPickerOpen && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              right: 0,
              minWidth: '100%',
              backgroundColor: 'var(--f-bg-raised)',
              border: '1px solid var(--f-border-strong)',
              borderRadius: 'var(--f-radius-lg)',
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
              padding: 4,
              zIndex: 50,
              overflow: 'hidden',
            }}
          >
            {unitOptions.map((u) => (
              <button
                key={u}
                type="button"
                onClick={() => selectUnit(u)}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '6px 12px',
                  border: 'none',
                  borderRadius: 'var(--f-radius-md)',
                  background: u === selectedUnit ? 'var(--f-accent-subtle)' : 'transparent',
                  color: u === selectedUnit ? 'var(--f-accent-text)' : 'var(--f-fg)',
                  fontFamily: 'var(--f-font-sans)',
                  fontSize: 'var(--f-text-sm)',
                  fontWeight: u === selectedUnit ? 600 : 400,
                  cursor: 'pointer',
                  textAlign: 'left',
                  whiteSpace: 'nowrap',
                  transition: 'background var(--f-duration-fast) ease',
                }}
                onMouseEnter={(e) => {
                  if (u !== selectedUnit) {
                    e.currentTarget.style.background = 'var(--f-bg-sunken)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background =
                    u === selectedUnit ? 'var(--f-accent-subtle)' : 'transparent';
                }}
              >
                {u}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  ) : unit ? (
    <>
      {divider}
      <span style={unitLabelStyle}>{unit}</span>
    </>
  ) : null;

  // ─── Input rows ────────────────────────────────────────────────────────────

  const inputRow =
    inputMode === 'stepper' ? (
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--f-space-3)' }}>
        <Button
          variant="outline"
          size="icon"
          aria-label="Decrease value"
          isDisabled={isDisabled || isGraded}
          onPress={() => handleStepperChange(-1)}
        >
          <Minus size={16} />
        </Button>
        <div
          style={{
            ...boxStyle,
            flex: 1,
            borderColor: focused ? 'var(--f-accent)' : 'var(--f-border)',
          }}
        >
          {unitPosition === 'prefix' && hasUnitAddon && unitAddon}
          <input
            type="text"
            inputMode="numeric"
            value={rawInput}
            onChange={handleInputChange}
            disabled={isDisabled || isGraded}
            placeholder={placeholder}
            style={inputStyle}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
          />
          {unitPosition === 'suffix' && hasUnitAddon && unitAddon}
        </div>
        <Button
          variant="outline"
          size="icon"
          aria-label="Increase value"
          isDisabled={isDisabled || isGraded}
          onPress={() => handleStepperChange(1)}
        >
          <Plus size={16} />
        </Button>
      </div>
    ) : (
      <div
        style={{
          ...boxStyle,
          borderColor: focused ? 'var(--f-accent)' : 'var(--f-border)',
        }}
      >
        {unitPosition === 'prefix' && hasUnitAddon && unitAddon}
        <input
          type="text"
          inputMode={integerOnly ? 'numeric' : 'decimal'}
          value={rawInput}
          onChange={handleInputChange}
          disabled={isDisabled || isGraded}
          placeholder={placeholder}
          style={inputStyle}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />
        {unitPosition === 'suffix' && hasUnitAddon && unitAddon}
      </div>
    );

  return (
    <StepShell>
      <PromptRenderer
        doc={snapshot.prompt as TiptapNode}
        media={mediaItems}
        diagramJson={diagramJson ?? null}
      />

      <div
        style={{
          opacity: isDisabled ? 0.7 : 1,
          pointerEvents: isDisabled || isGraded ? 'none' : 'auto',
        }}
      >
        {inputRow}

        {sigFigError && (
          <div
            style={{
              marginTop: 'var(--f-space-2)',
              fontSize: 'var(--f-text-xs)',
              color: 'var(--f-warning-text)',
              fontFamily: 'var(--f-font-sans)',
            }}
          >
            {sigFigError}
          </div>
        )}
      </div>

      {isGraded && result && <FeedbackDisplay result={result} />}
    </StepShell>
  );
}
